import { CampaignConfig } from 'schemas/CampaignConfig.schema.ts';
import { Iteration } from 'schemas/Iteration.schema.ts';

export type LabelDate = { day: string; month: string; year: string };

const COMPARATOR_NORMALISE_ALLOWED = [
  'OPTOUT',
  'LAST_SUCCESSFUL_COMMS_DATE',
  'LAST_VALID_DOSE_DATE',
  'INVALID_DOSES_COUNT',
  'VALID_DOSES_COUNT',
  'LAST_COMMS_CHANNEL',
  'LAST_COMMS_TYPE',
  'LAST_COMMS_BATCH',
  'LAST_COMMS_STATUS',
  'LAST_COMMS_DATE',
  'LAST_INVITE_COHORT',
  'LAST_INVITE_STATUS',
  'LAST_INVITE_BATCH',
  'LAST_INVITE_TYPE',
  'LAST_INVITE_CHANNEL',
  'LAST_INVITE_DATE',
  'BOOKED_APPOINTMENT_PROVIDER',
  'BOOKED_APPOINTMENT_DATE',
  'LAST_PROCEDURE_DATE',
  'LAST_PROCEDURE',
  'SUCCESSFUL_PROCEDURE_COUNT',
  'LAST_SUCCESSFUL_DOSE',
  'LAST_SUCCESSFUL_AMP',
  'DAYS_SINCE_LAST_SUCCESSFUL_PROCEDURE',
  'DAYS_SINCE_LAST_PROCEDURE',
  'LAST_SUCCESSFUL_DATE',
  'LAST_SUCCESSFUL_PROCEDURE',
  'PRIMARY_SERIES_COMPLETE',
  'TARGET',
  'REMOVAL_REASON_DATE',
  'REMOVAL_REASON_CODE',
  'LANGUAGE_PREFERENCE',
  'BSL_REQD',
  'LARGEPRINT_REQD',
  'AUDIO_REQD',
  'BRAILLE_REQD',
  'DE_FLAG',
  'CARE_HOME_FLAG',
  '13Q_FLAG',
  'TELEPHONE_FLAG',
  'MOBILE_FLAG',
  'EMAIL_FLAG',
  'COMMISSIONING_REGION',
  'SUBICB',
  'ICB',
  'PCN',
  'GP_PRACTICE_CODE',
  'POSTCODE_OUTCODE',
  'POSTCODE_SECTOR',
  'LOCAL_AUTHORITY',
  'LSOA',
  'MSOA',
  'POSTCODE',
  'GENDER',
  'DATE_OF_BIRTH',
  'DATE_JOINED',
  'COHORT_VERSION',
  'COHORT_LABEL',
];

const COLUMN_NAME_OVERRIDE_VALUES = new Map([
  ['ICB', 'ICB'],
  ['LSOA', 'LSOA'],
  ['MSOA', 'MSOA'],
  ['OPTOUT', 'Opt Out'],
  ['DE_FLAG', 'Detained Estate Flag'],
  ['13Q_FLAG', '13Q Flag'],
  ['PCN', 'PCN'],
  ['LD_FLAG', 'LD Flag'],
  ['GP_PRACTICE_CODE', 'GP Practice Code'],
  ['BSL_REQD', 'BSL Required'],
  ['BRAILLE_REQD', 'Braille Required'],
  ['AUDIO_REQD', 'Audio Required'],
  ['LARGEPRINT_REQD', 'Large Print Required'],
  ['LAST_SUCCESSFUL_AMP', 'Last Successful AMP'],
  ['DATE_OF_BIRTH', 'Date of Birth'],
  ['SUBICB', 'SubICB'],
]);

// Defining this type as it is not exported by nhsuk-react-components.
type StatusColour =
  | 'grey'
  | 'white'
  | 'blue'
  | 'red'
  | 'green'
  | 'aqua-green'
  | undefined;

export const getStatusColour = (status?: string): StatusColour => {
  if (!status) return 'red';

  const statusColourMap: Record<string, StatusColour> = {
    draft: 'white',
    'in review': 'blue',
    rejected: 'red',
    approved: 'green',
    'report requested': 'blue',
    'report ready': 'blue',
    'report in review': 'blue',
    'report rejected': 'red',
    'report approved': 'green',
    'sent to nhs notify': 'aqua-green',
  };

  const normalizedStatus = status.toLowerCase();
  return statusColourMap[normalizedStatus] ?? 'red';
};

export const formatStatus = (status: string) => {
  if (status.toLowerCase().includes('sent to nhs notify')) {
    return status.replace(/sent to nhs notify/gi, 'Sent to NHS Notify');
  }

  return status.charAt(0).toUpperCase() + status.slice(1);
};

export function campaignTypeToLabel(type: string): string {
  switch (type) {
    case 'V':
      return 'Vaccination';
    case 'S':
      return 'Screening';
    case 'A':
      return 'Awareness';
    default:
      return 'Unknown';
  }
}

export const vaccineConditionTargetLabels: Record<string, string> = {
  COVID: 'COVID-19',
  COVID_FLU: 'COVID-19 and Flu',
  COVID_RSV: 'COVID-19 and Respiratory syncytial virus (RSV)',
  FLU: 'Flu',
  MMR: 'Measles, mumps and rubella (MMR)',
  RSV: 'Respiratory syncytial virus (RSV)',
  MENB: 'MenB',
  MENACWY: 'MenACWY',
  HPV: 'Human Papillomavirus (HPV)',
  '3IN1': '3-in-1 (Td/IPV)',
  '3IN1_HPV_MENACWY_MMR': '16-24 Catch-up (3-in-1, HPV, MenACWY and MMR)',
};

export const awarenessConditionTargetLabels: Record<string, string> = {
  TALKINGTHERAPIES: 'Talking Therapies',
};

export const routingCommsTargetLabels: Record<string, string> = {
  '3IN1_HPV': '3-in-1 (Td/IPV) and Human Papillomavirus (HPV)',
  '3IN1_MENACWY': '3-in-1 (Td/IPV) and MenACWY',
  '3IN1_MMR': '3-in-1 (Td/IPV) and Measles, mumps and rubella (MMR)',
  HPV_MENACWY: 'Human Papillomavirus (HPV) and MenACWY',
  HPV_MMR: 'Human Papillomavirus (HPV) and Measles, mumps and rubella (MMR)',
  MENACWY_MMR: 'MenACWY and Measles, mumps and rubella (MMR)',
  '3IN1_HPV_MENACWY': '3-in-1 (Td/IPV), Human Papillomavirus (HPV) and MenACWY',
  '3IN1_HPV_MMR': '3-in-1 (Td/IPV), Human Papillomavirus (HPV) and Measles, mumps and rubella (MMR)',
  '3IN1_MENACWY_MMR': '3-in-1 (Td/IPV), MenACWY and Measles, mumps and rubella (MMR)',
  HPV_MENACWY_MMR: 'Human Papillomavirus (HPV), MenACWY and Measles, mumps and rubella (MMR)',
};

export function conditionTargetToLabel(target: string): string {
  return vaccineConditionTargetLabels[target] || awarenessConditionTargetLabels[target] || routingCommsTargetLabels[target] || 'N/A';
}

export function attributeLevelToLabel(level: string): string {
  switch (level) {
    case 'TARGET':
      return 'Target';
    case 'PERSON':
      return 'Person';
    case 'COHORT':
      return 'Cohort';
    default:
      return level;
  }
}

export function iterationTypeToLabel(iterationType: string): string {
  switch (iterationType) {
    case 'A':
      return 'Automatic';
    case 'M':
      return 'Manual';
    case 'S':
      return 'Supervised';
    default:
      return iterationType;
  }
}

export function commsTypeToLabel(commsType: string): string {
  switch (commsType) {
    case 'I':
      return 'First communication';
    case 'R':
      return 'Reminder communication';
    default:
      return commsType;
  }
}

export function ruleTypeToLabel(ruleType: string): string {
  switch (ruleType) {
    case 'F':
      return 'filter';
    case 'S':
      return 'suppression';
    case 'R':
      return 'routing';
    default:
      return ruleType;
  }
}

export function validateDateLengths(date: LabelDate): string | undefined {
  const { day, month, year } = date;
  if (day.length > 2) {
    return 'Day cannot be longer than 2 digits';
  }
  if (month.length > 2) {
    return 'Month cannot be longer than 2 digits';
  }
  if (year.length > 4) {
    return 'Year cannot be longer than 4 digits';
  }
  return undefined;
}

export function deriveDisplayName(columnName: string): string {
  if (COLUMN_NAME_OVERRIDE_VALUES.has(columnName)) {
    return COLUMN_NAME_OVERRIDE_VALUES.get(columnName) ?? columnName;
  }
  const nameSplit = columnName
    .toLowerCase()
    .split('_')
    .map(
      (namePart: string) => `${namePart[0].toUpperCase()}${namePart.slice(1)}`,
    );
  return nameSplit.join(' ');
}

export function YYYYMMDDToLabel(date?: string): LabelDate {
  if (!date) {
    return { day: '', month: '', year: '' };
  }

  const year = date.substring(0, 4);
  const month = date.substring(4, 6);
  const day = date.substring(6, 8);

  return {
    day,
    month,
    year,
  };
}

// The NHSUK date picker component uses { day, month, year }, which needs converting back to YYYYMMDD
export function labelToYYYYMMDD({ day, month, year }: LabelDate): string {
  return `${year.padStart(4, '0')}${month.padStart(2, '0')}${day.padStart(2, '0')}`;
}

export function YYYYMMDDToHumanReadable(
  YYYYMMDDdate?: string,
): string | undefined {
  if (!YYYYMMDDdate) {
    return undefined;
  }

  const year = YYYYMMDDdate.substring(0, 4);
  const month = YYYYMMDDdate.substring(4, 6);
  const day = YYYYMMDDdate.substring(6, 8);

  const date = new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
  );

  return `${day} ${date.toLocaleDateString('default', { month: 'long' })} ${year}`;
}

export function campaignDateFormat(
  date: string | undefined,
  formatType: 'iteration' | 'campaignRange',
  endDate?: string,
): string | undefined {
  const parseDate = (dateStr: string | undefined): Date | undefined => {
    if (!dateStr || !/^\d{8}$/.test(dateStr)) {
      return undefined;
    }
    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10) - 1;
    const day = parseInt(dateStr.substring(6, 8), 10);
    return new Date(year, month, day);
  };

  const parsedDate = parseDate(date);
  const parsedEndDate = endDate ? parseDate(endDate) : undefined;

  if (!parsedDate) return undefined;

  if (formatType === 'iteration') {
    const weekday = parsedDate.toLocaleDateString('en-GB', {
      weekday: 'short',
    });
    const day = parsedDate.getDate();
    const month = parsedDate.toLocaleDateString('en-GB', {
      month: 'short',
    });
    const year = parsedDate.getFullYear();

    return `${weekday} ${day} ${month} ${year}`;
  }

  if (formatType === 'campaignRange' && parsedEndDate) {
    const startFormat = parsedDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year:
        parsedDate.getFullYear() !== parsedEndDate.getFullYear()
          ? 'numeric'
          : undefined,
    });
    const endFormat = parsedEndDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    return `${startFormat} to ${endFormat}`;
  }

  return undefined;
}

export function YYYYMMDDToUnixDate(dateString: string): number {
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);

  const date = new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
  );

  return date.getTime();
}

export function labelToUnixDate(labelDate: {
  day: string;
  month: string;
  year: string;
}): number {
  const date = new Date(
    parseInt(labelDate.year, 10),
    parseInt(labelDate.month, 10) - 1,
    parseInt(labelDate.day, 10),
  );

  return date.getTime();
}

// 2024073008373801 -> July 30, 2024 08:37:38
export function YYYYMMDDHHMMSSmmToHumanReadable(dateString: string): string {
  const year = dateString.substring(0, 4);
  const month = dateString.substring(4, 6);
  const day = dateString.substring(6, 8);
  const hour = dateString.substring(8, 10);
  const minute = dateString.substring(10, 12);
  const second = dateString.substring(12, 14);

  const date = new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10),
    parseInt(hour, 10),
    parseInt(minute, 10),
    parseInt(second, 10),
  );

  return `${date.toLocaleDateString('default', { month: 'long' })} ${day}, ${year} ${hour}:${minute}:${second}`;
}

export function sanitizedIteration(iteration: Iteration): Iteration {
  const clonedIteration = structuredClone(iteration);
  clonedIteration.Version = iteration.Version ? iteration.Version + 1 : 1;
  const iterationRules = iteration.IterationRules || [];

  if (iterationRules.length > 0) {
    const sanitizedRules = iterationRules
      .sort((a, b) => (a.Priority ?? 0) - (b.Priority ?? 0))
      .map((rule) => {
        const sanitizedRule = structuredClone(rule);
        delete sanitizedRule.AndNumber;
        delete sanitizedRule.ID;
        delete sanitizedRule.LibraryRuleMetadata;

        if (sanitizedRule.AttributeTarget === 'ALL') {
          delete sanitizedRule.AttributeTarget;
        }

        return sanitizedRule;
      });

    clonedIteration.IterationRules = sanitizedRules;
  }

  return clonedIteration;
}

export function prepareCampaignForExport(
  campaign: CampaignConfig,
): CampaignConfig {
  const campaignToExport = structuredClone(campaign);

  campaignToExport.CampaignConfig.Version = campaignToExport.CampaignConfig
    .Version
    ? campaignToExport.CampaignConfig.Version + 1
    : 1;

  const {
    // eslint-disable-next-line camelcase
    locked_by,
    // eslint-disable-next-line camelcase
    created_by,
    // eslint-disable-next-line camelcase
    creation_timestamp,
    // eslint-disable-next-line camelcase
    last_modified_by,
    // eslint-disable-next-line camelcase
    last_modified_timestamp,
    // eslint-disable-next-line camelcase
    iteration_details,
    ...cleanCampaign
  } = campaignToExport;

  const campaignIterations = cleanCampaign.CampaignConfig.Iterations || [];

  cleanCampaign.CampaignConfig.Iterations = campaignIterations.map(
    (iteration) => sanitizedIteration(iteration),
  );

  return cleanCampaign;
}

export function unixTimestampToHumanReadableDate(timestamp: number): string {
  if (!timestamp) {
    return '';
  }

  const date = new Date(Number(timestamp));
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleDateString('en-GB', { month: 'long' });
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
}

export function unixTimestampToHumanReadableTime(timestamp: number): string {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  const hours12 = hours % 12 || 12;

  return `${hours12}:${minutes}${ampm}`;
}

export function formatTeamId(id: string): string {
  const specialAcronyms = ['covid', 'rsv', 'mmr'];
  const words = id.split('_');
  if (words.length === 0) return '';

  return words.map((word, index) => {
    if (index > 0 && specialAcronyms.includes(word.toLowerCase())) {
      return word.toUpperCase();
    }
    return index === 0
      ? word.charAt(0).toUpperCase() + word.slice(1)
      : word;
  }).join(' ');
}

export const normaliseCommaSeparatedListWithAttributeToIgnoreWhitelist = (
  attributeName: string | undefined,
  value?: string,
): string | undefined => {
  if (!value) return value;

  if (!attributeName || !COMPARATOR_NORMALISE_ALLOWED.includes(attributeName)) {
    return value;
  }

  return value
    .split(',')
    .map((v) => v.trim())
    .join(',');
};

export function capitaliseFirstChar(value: string | undefined): string {
  if (!value) {
    return value ?? '';
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function getMessagePlanStatusColour(status: string): StatusColour {
  if (status === 'production') return 'green';
  if (status === 'legacy') return 'blue';
  return 'white';
}
